import React, { useEffect, useState } from 'react';

import { Document as PDFDocument, Page } from 'react-pdf';

import { AlertTriangle, Eye, Lock, RefreshCw, Upload, X } from 'lucide-react';

import { ensureUtcTimestamp } from '../../utils/utils';
import { PDF_DOCUMENT_OPTIONS } from '../DocumentViewer';

import { documentCheckTitle, type UploadAction } from './DocumentCheckBanner';
import { pageSlots } from './statementPages';

import type { DocumentCheck } from '../../api';

// v9 .btn / .btn-sm / .btn-primary / .btn-secondary / .btn-link
const BTN =
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-medium leading-5 transition-colors [&_svg]:h-4 [&_svg]:w-4 [&_svg]:flex-none';
const BTN_SM =
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3 py-1 text-xs font-medium leading-[18px] transition-colors [&_svg]:h-3 [&_svg]:w-3 [&_svg]:flex-none';
const PRIMARY =
	'border-transparent bg-primary-500 text-white hover:bg-primary-600';
const SECONDARY = 'border-gray-300 bg-white text-primary-700 hover:bg-gray-50';
const LINK = 'border-0 bg-transparent p-0 text-sm font-medium text-primary-700';

const ADD_PAGES: UploadAction = {
	label: 'Add the missing pages',
	mode: 'append',
};
const NEW_FILE: UploadAction = { label: 'Upload a new file', mode: 'replace' };

type Severity = 'proc' | 'stop' | 'hard';

// v9 statusOf(): the pill for a statement that did not reach the analysis.
const STATES: [code: string, label: string, severity: Severity][] = [
	['NOT_A_STATEMENT', 'Not a statement', 'stop'],
	['MISSING_PAGES', 'Needs pages', 'hard'],
	['UNREADABLE', 'New file needed', 'hard'],
];

export const statementState = (
	processing: boolean,
	check?: DocumentCheck | null,
): { label: string; severity: Severity } => {
	if (processing) return { label: 'Processing', severity: 'proc' };
	const codes = new Set<string>(check?.issues.map((issue) => issue.code));
	const [, label, severity] = STATES.find(([code]) => codes.has(code)) ?? [
		'',
		'New file needed',
		'hard',
	];
	return { label, severity };
};

// v9 .chk.proc / .chk.stop / .chk.hard
const CHIP: Record<Severity, string> = {
	proc: 'border-blue-100 bg-blue-50 text-blue-700',
	stop: 'border-red-100 bg-red-50 text-red-700',
	hard: 'border-sand-300 bg-sand-100 text-sand-900',
};
const CHIP_ICON: Record<Severity, React.ElementType> = {
	proc: RefreshCw,
	stop: X,
	hard: AlertTriangle,
};
// v9 .cbanner (processing uses the inline blue style from banner())
const BANNER: Record<Severity, { box: string; icon: string }> = {
	proc: {
		box: 'border-blue-100 bg-[#f3faff] text-blue-700',
		icon: 'bg-blue-100 text-blue-700',
	},
	stop: {
		box: 'border-red-100 bg-red-50 text-red-700',
		icon: 'bg-red-100 text-red-700',
	},
	hard: {
		box: 'border-[#ffd59e] bg-[#fff7ec] text-sand-900',
		icon: 'bg-sand-100 text-sand-900',
	},
};

const toDate = (timestamp?: string): Date | null => {
	if (!timestamp) return null;
	const date = new Date(ensureUtcTimestamp(timestamp));
	return Number.isNaN(date.getTime()) ? null : date;
};
const clock = (date: Date) =>
	date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
/** "Today, 10:05 AM" as in v9, or "Nov 3, 10:05 AM" for an earlier day. */
const when = (date: Date) =>
	`${
		date.toDateString() === new Date().toDateString()
			? 'Today'
			: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
	}, ${clock(date)}`;
const elapsed = (from: Date, now: number) => {
	const seconds = Math.max(0, Math.floor((now - from.getTime()) / 1000));
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const plural = (count: number, word: string) =>
	`${count} ${word}${count === 1 ? '' : 's'}`;
const pagesText = (pages: number[]) =>
	pages.length > 1
		? `Pages ${pages.slice(0, -1).join(', ')} and ${pages[pages.length - 1]}`
		: `Page ${pages[0]}`;

interface StatementStateViewProps {
	fileName: string;
	processing: boolean;
	check?: DocumentCheck | null;
	uploadedAt?: string;
	uploadedBy?: { name: string; email: string };
	statementMonth?: string;
	pdfUrl?: string;
	onBack: () => void;
	onViewStatement: () => void;
	onRefresh: () => void;
	/** Opens the add-pages / replace upload; hidden while the file is processing. */
	onUpload?: (action: UploadAction) => void;
}

/** Processing and bounced statements in the v9 case layout (st-pine/linden/willow/bayview). */
const StatementStateView: React.FC<StatementStateViewProps> = ({
	fileName,
	processing,
	check,
	uploadedAt,
	uploadedBy,
	statementMonth,
	pdfUrl,
	onBack,
	onViewStatement,
	onRefresh,
	onUpload,
}) => {
	const [filePages, setFilePages] = useState(0);
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!processing) return undefined;
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [processing]);

	// A rerun is checking a new file, so the last check no longer applies.
	const flagged =
		!processing && check?.status === 'flagged' && check.issues.length > 0;
	const codes = new Set<string>(flagged ? check.issues.map((i) => i.code) : []);
	const slots = pageSlots(filePages, flagged ? check : null);
	const missing = slots.filter((s) => s.state === 'missing').map((s) => s.page);
	const blurred = slots
		.filter((s) => s.state === 'unreadable')
		.map((s) => s.page);
	const toReview = missing.length + blurred.length;
	const { label, severity } = statementState(
		processing,
		flagged ? check : null,
	);
	const ChipIcon = CHIP_ICON[severity];
	const received = toDate(uploadedAt);
	const checkedAt = flagged ? toDate(check.checked_at) : null;
	const primaryAction = codes.has('MISSING_PAGES') ? ADD_PAGES : NEW_FILE;

	const sub = processing
		? `${fileName}${filePages ? ` · ${plural(filePages, 'page')}` : ''}`
		: codes.has('NOT_A_STATEMENT')
			? 'This file does not look like a processing statement. A processing statement lists the fees the processor charged.'
			: codes.has('MISSING_PAGES') && missing.length && filePages
				? `The statement says it has ${slots.length} pages. The file has ${filePages}.`
				: codes.has('UNREADABLE') && blurred.length
					? `${pagesText(blurred)} ${blurred.length > 1 ? 'are' : 'is'} too blurred to read the fee figures.`
					: (check?.issues[0]?.reason ?? '');

	const banner = BANNER[severity];
	const bannerTitle = flagged ? documentCheckTitle(check) : 'Checking the file';
	const bannerReason = flagged
		? check.issues.map((issue) => issue.reason).join(' ')
		: '';
	const events = [
		{
			title: 'Received',
			detail: uploadedBy?.name ? `Uploaded by ${uploadedBy.name}` : 'Uploaded',
			time: received ? clock(received) : '',
			// v9 caseEvents(): a "Checked" line that is not about missing pages is green.
			ok: false,
		},
		...(flagged
			? [
					{
						title: 'Checked',
						detail: `${documentCheckTitle(check)}.`,
						time: checkedAt ? clock(checkedAt) : '',
						ok: !codes.has('MISSING_PAGES'),
					},
				]
			: []),
	];

	return (
		<>
			<div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4">
				<div className="flex min-w-0 flex-auto items-center gap-4">
					<button
						type="button"
						className={`inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium leading-[18px] ${SECONDARY}`}
						onClick={onBack}
					>
						Back
					</button>
					<div className="min-w-0">
						<div className="break-words text-lg font-semibold text-gray-900">
							{fileName}
						</div>
						<div className="mt-2 flex flex-wrap items-center gap-2">
							<span
								className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs font-medium leading-[1.3] [&_svg]:h-3 [&_svg]:w-3 ${CHIP[severity]}`}
							>
								<ChipIcon aria-hidden="true" />
								{label}
							</span>
							<span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-medium leading-[1.3] text-gray-600">
								—
							</span>
							{received && (
								<span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-medium leading-[1.3] text-gray-600">
									Uploaded · {when(received)}
								</span>
							)}
						</div>
					</div>
				</div>
				<div className="flex flex-wrap items-center justify-end gap-3">
					<button
						type="button"
						className={`${BTN} ${SECONDARY}`}
						onClick={onViewStatement}
					>
						<Eye aria-hidden="true" />
						View statement
					</button>
					{processing ? (
						<button
							type="button"
							className={`${BTN} ${SECONDARY}`}
							onClick={onRefresh}
						>
							<RefreshCw aria-hidden="true" />
							Refresh
						</button>
					) : (
						onUpload && (
							<button
								type="button"
								className={`${BTN} ${PRIMARY}`}
								onClick={() => onUpload(primaryAction)}
							>
								<Upload aria-hidden="true" />
								{primaryAction.label}
							</button>
						)
					)}
				</div>
			</div>

			{(processing || flagged) && (
				<div
					className={`flex items-start gap-4 border-b px-6 py-5 ${banner.box}`}
				>
					<span
						className={`flex h-10 w-10 flex-none items-center justify-center rounded-full [&_svg]:h-5 [&_svg]:w-5 ${banner.icon}`}
					>
						<ChipIcon aria-hidden="true" />
					</span>
					<div>
						<div className="text-base font-semibold">{bannerTitle}</div>
						<div className="mt-1 max-w-[80ch] text-sm">
							{processing ? (
								<>
									We check that the file is a complete, readable processing
									statement before we analyze it.{' '}
									{received ? (
										<>
											Running for <b>{elapsed(received, now)}</b>.
										</>
									) : (
										'This takes about a minute.'
									)}
								</>
							) : (
								bannerReason
							)}
						</div>
						{flagged && onUpload && (
							<div className="mt-3.5 flex flex-wrap gap-2">
								{codes.has('MISSING_PAGES') && (
									<button
										type="button"
										className={`${BTN_SM} ${PRIMARY}`}
										onClick={() => onUpload(ADD_PAGES)}
									>
										<Upload aria-hidden="true" />
										{ADD_PAGES.label}
									</button>
								)}
								<button
									type="button"
									className={`${BTN_SM} ${SECONDARY}`}
									onClick={() => onUpload(NEW_FILE)}
								>
									<Upload aria-hidden="true" />
									{NEW_FILE.label}
								</button>
								<button
									type="button"
									className={`${BTN_SM} ${SECONDARY}`}
									onClick={onViewStatement}
								>
									<Eye aria-hidden="true" />
									View statement
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			<div className="mx-auto max-w-[1440px] px-8 pb-16 pt-6">
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
					<div className="space-y-4">
						<section className="rounded-lg border border-gray-200 bg-white p-6">
							<h3 className="mb-1 text-base font-semibold text-gray-900">
								What we received
							</h3>
							<div className="mb-4 text-sm text-gray-500">{sub}</div>
							{pdfUrl ? (
								<PDFDocument
									file={pdfUrl}
									options={PDF_DOCUMENT_OPTIONS}
									onLoadSuccess={({ numPages }) => setFilePages(numPages)}
									loading={
										<p className="text-sm text-gray-500">Loading pages…</p>
									}
									error={
										<p className="text-sm text-gray-500">
											The pages cannot be shown here. Use View statement.
										</p>
									}
								>
									<div className="flex flex-wrap gap-4">
										{slots.map((slot) =>
											slot.filePage ? (
												<button
													key={slot.page}
													type="button"
													className="group relative flex w-[104px] flex-col items-center gap-1.5"
													onClick={onViewStatement}
													aria-label={`Open page ${slot.filePage}`}
												>
													<span
														className={`relative block aspect-[900/1273] w-[104px] overflow-hidden rounded-sm border bg-white ${
															slot.state === 'unreadable'
																? 'border-red-500 shadow-[0_0_0_2px] shadow-red-100'
																: 'border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,.12)] group-hover:border-blue-500 group-hover:shadow-[0_0_0_2px] group-hover:shadow-blue-100'
														}`}
													>
														<Page
															pageNumber={slot.filePage}
															width={104}
															renderTextLayer={false}
															renderAnnotationLayer={false}
														/>
													</span>
													{slot.state === 'unreadable' && (
														<span className="absolute bottom-[22px] left-1.5 right-1.5 rounded border border-red-100 bg-red-50 px-1 py-[3px] text-center text-[10px] font-semibold leading-[1.2] text-red-700">
															Too blurred to read
														</span>
													)}
													<span className="text-xs leading-none text-gray-500">
														{slot.filePage}
													</span>
												</button>
											) : (
												<div
													key={slot.page}
													className="flex w-[104px] flex-col items-center gap-1.5"
												>
													<span className="flex aspect-[900/1273] w-[104px] items-center justify-center rounded-sm border-2 border-dashed border-[#ff9d1c] bg-[#fff7ec] p-2 text-center text-[11px] font-semibold text-sand-900">
														Page {slot.page} is not in the file
													</span>
													<span className="text-xs leading-none text-gray-500">
														Missing
													</span>
												</div>
											),
										)}
									</div>
								</PDFDocument>
							) : (
								<p className="text-sm text-gray-500">
									{processing
										? 'The pages appear here once the file is ready.'
										: 'The pages cannot be shown here. Use View statement.'}
								</p>
							)}
							{filePages > 0 && (
								<div className="mt-3 flex flex-wrap items-center gap-3">
									<span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
										{plural(filePages, 'page')}
										{toReview > 0 && ` · ${toReview} to review`}
									</span>
									<button
										type="button"
										className={LINK}
										onClick={onViewStatement}
									>
										Review pages
									</button>
								</div>
							)}
						</section>
						{!processing && (
							<div className="flex items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
								<Lock
									className="h-[18px] w-[18px] flex-none"
									aria-hidden="true"
								/>
								<div>
									The savings, the quote and the export appear when a complete,
									readable statement is processed.
								</div>
							</div>
						)}
					</div>

					<div className="space-y-4">
						<section className="rounded-lg border border-gray-200 bg-white p-6">
							<h3 className="mb-1 text-base font-semibold text-gray-900">
								Merchant
							</h3>
							<dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-5 gap-y-1.5 text-sm">
								<dt className="text-gray-500">Contact</dt>
								<dd>—</dd>
								<dt className="text-gray-500">Email</dt>
								<dd>—</dd>
								<dt className="text-gray-500">Rep</dt>
								<dd>{uploadedBy?.name || '—'}</dd>
								<dt className="text-gray-500">Partner</dt>
								<dd>—</dd>
								<dt className="text-gray-500">Processor</dt>
								<dd>—</dd>
							</dl>
						</section>

						<section className="rounded-lg border border-gray-200 bg-white p-6">
							<h3 className="mb-1 text-base font-semibold text-gray-900">
								Case activity
							</h3>
							<div className="mb-4 text-sm text-gray-500">
								One case: {fileName}
								{statementMonth ? ` · ${statementMonth}` : ''}.
							</div>
							<ol className="mt-3">
								{events.map((event, index) => (
									<li
										key={event.title}
										className="relative flex items-start gap-3 pb-4"
									>
										{index < events.length - 1 && (
											<span className="absolute bottom-[-2px] left-[5px] top-3.5 border-l-2 border-gray-200" />
										)}
										<span
											className={`relative z-[1] mt-[5px] h-3 w-3 flex-none rounded-full border-2 ${
												event.ok
													? 'border-[#a2e63c] bg-[#a2e63c]'
													: 'border-gray-300 bg-white'
											}`}
										/>
										<div className="min-w-0 flex-1">
											<div className="text-sm font-medium text-gray-900">
												{event.title}
											</div>
											<div className="mt-0.5 break-words text-xs text-gray-500">
												{event.detail}
											</div>
										</div>
										<span className="whitespace-nowrap pt-0.5 text-xs text-gray-500">
											{event.time}
										</span>
									</li>
								))}
							</ol>
						</section>
					</div>
				</div>
			</div>
		</>
	);
};

export default StatementStateView;
